import { useEffect, useMemo, useState, memo, useRef, useCallback } from 'react';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import type { Engine, ISourceOptions, Container } from '@tsparticles/engine';
import { loadSlim } from '@tsparticles/slim';
import { useTheme } from '../../lib/useTheme';
import { posthogEvent } from '../../lib/tracking';

type NavigatorWithDeviceInfo = Navigator & {
  deviceMemory?: number;
  connection?: { saveData?: boolean };
};

type LowEndReason =
  | 'prefers_reduced_motion'
  | 'update_slow'
  | 'save_data'
  | 'low_device_memory'
  | 'low_hardware_concurrency';

function detectLowEndUpfront(): LowEndReason | null {
  if (typeof window === 'undefined') return null;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'prefers_reduced_motion';
  if (window.matchMedia('(update: slow)').matches) return 'update_slow';
  const nav = navigator as NavigatorWithDeviceInfo;
  if (nav.connection?.saveData) return 'save_data';
  if (typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 2) return 'low_device_memory';
  if (typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency <= 2) return 'low_hardware_concurrency';
  return null;
}

// Probe actual frame rate for ~1s. Resolves true if sustained FPS is too low.
function probeLowFps(thresholdFps = 24, sampleMs = 1000): Promise<boolean> {
  return new Promise((resolve) => {
    let frames = 0;
    let start = 0;
    const tick = (t: number) => {
      if (!start) start = t;
      frames++;
      if (t - start >= sampleMs) {
        const fps = (frames * 1000) / (t - start);
        resolve(fps < thresholdFps);
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

type ParticleBackgroundProps = {
  id?: string;
  className?: string;
  /**
   * Controls how blurry/soft the blobs look. Defaults to 60px.
   */
  blurPx?: number;
  /**
   * Opacity of the blurred layer. Defaults to 0.35.
   */
  opacity?: number;
  /**
   * Z-index for the fixed layer. Defaults to -1.
   */
  zIndex?: number;
  /**
   * Maximum number of particles to render. Defaults to 5.
   */
  maxParticles?: number;
  /**
   * Max height (any CSS length) of the particle rendering layer. When set, it
   * overrides the default responsive height. Defaults to undefined.
   */
  maxHeight?: string;
  /**
   * Vertical offset (any CSS length) that translates the layer in the y
   * direction. Positive values move it down. Defaults to undefined.
   */
  offsetY?: string;
};

// Global flag to ensure particles engine is only initialized once
let engineInitialized = false;

/**
 * Fixed, full-screen background using tsParticles to render slow, soft "blob" circles.
 * Colors roughly match the palette shown in the provided Framer settings.
 */
const ParticleBackground = memo(function ParticleBackground({
  id = 'tsparticles-blobs',
  className,
  blurPx = 100,
  opacity: opacityProp,
  zIndex = -1,
  maxParticles = 5,
  maxHeight,
  offsetY = '-50%',
}: ParticleBackgroundProps) {
  const [init, setInit] = useState(engineInitialized);
  const [lowEndReason, setLowEndReason] = useState<LowEndReason | 'low_fps' | null>(
    () => detectLowEndUpfront(),
  );
  const loggedModeRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === 'dark';
  const opacity = opacityProp ?? (isDark ? 0.9 : 0.95);

  // Log the chosen mode once per mount (and again if it changes after FPS probe).
  useEffect(() => {
    const mode = lowEndReason ? 'static' : 'animated';
    const key = `${mode}:${lowEndReason ?? ''}`;
    if (loggedModeRef.current === key) return;
    loggedModeRef.current = key;
    posthogEvent('particle_background_mode', {
      mode,
      reason: lowEndReason,
      device_memory: (navigator as NavigatorWithDeviceInfo).deviceMemory,
      hardware_concurrency: navigator.hardwareConcurrency,
    });
    console.log('LowEnd', lowEndReason);
  }, [lowEndReason]);

  useEffect(() => {
    if (lowEndReason) return;
    if (!engineInitialized) {
      initParticlesEngine(async (engine: Engine) => {
        await loadSlim(engine);
      }).then(() => {
        engineInitialized = true;
        setInit(true);
      });
    }
  }, [lowEndReason]);

  // After mount, probe real frame rate. Downgrade to static if too slow.
  useEffect(() => {
    if (lowEndReason || !init) return;
    let cancelled = false;
    probeLowFps().then((slow) => {
      if (!cancelled && slow) setLowEndReason('low_fps');
    });
    return () => {
      cancelled = true;
    };
  }, [lowEndReason, init]);

  // Block tsParticles' window resize listener when only the height changes
  // (Android nav bar / soft keyboard show-hide fires resize with the same width).
  useEffect(() => {
    if (!init || lowEndReason) return;
    let lastWidth = window.innerWidth;
    const handleResize = (e: Event) => {
      const currentWidth = window.innerWidth;
      if (currentWidth === lastWidth) {
        // Height-only change — stop tsParticles from seeing it
        e.stopImmediatePropagation();
      }
      lastWidth = currentWidth;
    };
    window.addEventListener('resize', handleResize, true);
    return () => window.removeEventListener('resize', handleResize, true);
  }, [init, lowEndReason]);

  // Callback when particles are loaded - disable resize observer
  const particlesLoaded = useCallback(async (container?: Container) => {
    if (container) {
      // Override the resize handler to do nothing
      container.canvas.resize = () => false;
    }
  }, []);

  const options = useMemo<ISourceOptions>(() => {
    return {
      style: {
        position: 'absolute',
        inset: '0',
        top: offsetY,
      },
      detectRetina: false,
      fpsLimit: 15,
      background: {
        color: { value: 'transparent' },
      },
      fullScreen: false,
      // Disable responsive behavior to prevent rerenders on resize
      responsive: [],
      // Disable auto-resize to prevent canvas updates on window resize
      autoRefresh: false,
      particles: {
        number: {
          value: maxParticles,
          // Disable density to prevent recalculation on resize
          density: { enable: false },
        },
        color: {
          value: isDark 
            ? ['#81358a', '#ef6225', '#872800', ] // deep purple, teal, magenta, orange dominant
            : ['#aa45b5', '#ef6225', '#a63d11', '#ef6225'], // teal, magenta, orange dominant
        },
        shape: {
          type: 'circle',
        },
        opacity: {
          value: 1,
        },
        size: {
          value: { min: 120, max: 150 },
        },
        links: {
          enable: false,
        },
        move: {
          enable: true,
          direction: 'none',
          speed: 2, // slow drift
          random: false,
          straight: false,
          // keep particles inside the canvas
          outModes: { default: 'bounce' },
        },
        zIndex: {
          value: 0,
        },
      },
    };
  }, [isDark, maxParticles]);

  if (lowEndReason) {
    const blobs = isDark
      ? [
          'radial-gradient(circle at 20% 30%, #3d2151 0%, transparent 35%)',
          'radial-gradient(circle at 75% 25%, #81358a 0%, transparent 35%)',
          'radial-gradient(circle at 30% 75%, #36a2ad 0%, transparent 35%)',
          'radial-gradient(circle at 80% 80%, #ef6225 0%, transparent 35%)',
        ]
      : [
          'radial-gradient(circle at 20% 30%, #36a2ad 0%, transparent 35%)',
          'radial-gradient(circle at 75% 25%, #81358a 0%, transparent 35%)',
          'radial-gradient(circle at 30% 75%, #5f84ff 0%, transparent 35%)',
          'radial-gradient(circle at 80% 80%, #ef6225 0%, transparent 35%)',
        ];
    return (
      <div
        className={`${className} -left-[25vmax] -right-[25vmax] -top-[100px] ${maxHeight ? '' : 'h-[30vh]'}`}
        style={{
          position: 'fixed',
          filter: `blur(${blurPx}px)`,
          opacity: `${opacity}`,
          zIndex,
          backgroundImage: blobs.join(', '),
          ...(maxHeight ? { height: maxHeight } : {}),
          ...(offsetY ? { transform: `translateY(${offsetY})` } : {}),
        }}
      />
    );
  }

  if (!init) {
    return null;
  }
  return (
    <div
      ref={containerRef}
      className={`${className} -left-[10vmax] -right-[10vmax] md:left-[10vmax] md:right-[10vmax] ${maxHeight ? '' : 'md:h-[200px] h-[250px]'}`}
      style={{
        position: 'fixed',
        filter: `blur(${blurPx}px)`,
        opacity: `${opacity}`,
        zIndex,
        ...(maxHeight ? { height: maxHeight } : {}),
      }}
    >
      <Particles
        key={effectiveTheme}
        id={`${id}-${effectiveTheme}`}
        options={options}
        particlesLoaded={particlesLoaded}
      />
    </div>
  );
});

export default ParticleBackground;
